import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { cartPerformance } from '@theme/performance';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { DeliveryCartAttributes } from './delivery-cart-attributes.js';
import { openPostalCodeDrawer } from './delivery-core.js';
import { storePendingProductData } from './meal-delivery-pending-product.js';

// Constants
const CONSOLE_PREFIX = '[PRODUCT-FORM]';

// Helper Functions
/**
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * Shared function to handle product form submission logic
 * @param {HTMLFormElement} form - The form element
 * @param {Event} event - The event object
 */
async function handleProductSubmission(form, event) {
  logMessage('log', 'Product submission triggered');
  
  // Get form data first
  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const productIdInput = form.querySelector('input[name="product-id"]');

  if (!variantInput) {
    logMessage('error', 'No variant input found');
    return false;
  }
  
  const variantId = /** @type {HTMLInputElement} */ (variantInput).value;
  const quantity = quantityInput ? /** @type {HTMLInputElement} */ (quantityInput).value : '1';
  const productId = productIdInput ? /** @type {HTMLInputElement} */ (productIdInput).value : '';

  
  // Check postal code and delivery time FIRST
  let hasCompleteData;
  let cartData = null;
  
  if (/** @type {any} */ (window).deliveryManager) {
    const result = await /** @type {any} */ (window).deliveryManager.hasCompleteDeliveryInfo();
    hasCompleteData = result.hasData;
    cartData = result.cart;
  } else {
    const result = await DeliveryCartAttributes.hasPostalCodeAndDeliveryTime();
    hasCompleteData = result.hasData;
    cartData = result.cart;
  }
  
  logMessage('log', 'Checking postal code and delivery time:', hasCompleteData);
  logMessage('log', 'Cart data available:', !!cartData);
  
  if (!hasCompleteData) {
    // Check if postal code form is already filled (from customer metafield)
    const postcodeForm = document.querySelector('#postcode-form');
    const digitsInput = postcodeForm?.querySelector('[name="postcode_digits"]');
    const lettersInput = postcodeForm?.querySelector('[name="postcode_letters"]');
    
    const hasFilledPostcode = /** @type {HTMLInputElement} */ (digitsInput)?.value && /** @type {HTMLInputElement} */ (lettersInput)?.value;
    logMessage('log', 'Has filled postal code in form:', hasFilledPostcode);
    
    if (hasFilledPostcode) {
      // Postal code is pre-filled - auto-submit the form
      logMessage('log', 'Postal code is pre-filled, auto-submitting form');
      
      // Get the product ID from the current product card or product-form-component
      const productCard = form.closest('product-card');
      const productFormComponent = form.closest('product-form-component');
      const productId = productCard?.getAttribute('data-product-id') || productFormComponent?.getAttribute('data-product-id') || '';
      
      logMessage('log', 'Found variant ID:', variantId);
      storePendingProductData(variantId, quantity, productId);
      
      // Auto-submit the postal code form
      if (postcodeForm) {
        logMessage('log', 'Auto-submitting postal code form');
        /** @type {HTMLFormElement} */ (postcodeForm).requestSubmit();
      }
      
      // Prevent default form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    } else {
      // No postal code - open postal code drawer
      logMessage('log', 'No postal code found, opening postal drawer');
      
      const productCard = form.closest('product-card');
      const productFormComponent = form.closest('product-form-component');
      const productId = productCard?.getAttribute('data-product-id') || productFormComponent?.getAttribute('data-product-id') || '';
      
      logMessage('log', 'Found variant ID:', variantId);
      storePendingProductData(variantId, quantity, productId);
      openPostalCodeDrawer();
      
      // Prevent default form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    }
  }
  
  // const cartData = await /** @type {any} */ (window).DeliveryCartAttributes.getCartData();
  
  // Check if product is compatible with current delivery day
  if (productId) {
    // Use the cart data we already fetched to avoid redundant API calls
    logMessage('log', 'Checking product compatibility with cart data:', !!cartData);
    const isCompatible = await /** @type {any} */ (window).ProductCompatibility?.isProductCompatibleWithDeliveryDay(productId, cartData);
    if (!isCompatible) {
      // Show incompatibility popup
      await /** @type {any} */ (window).ProductCompatibility?.showIncompatibilityPopup(productId);
      
      // Prevent form submission
      if (event) {
        event.preventDefault();
      }
      return false;
    }
  }
  
  return true;
}



/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows loading state on the add to cart button.
   */
  showLoading() {
    const button = this.refs.addToCartButton;
    if (button) {
      button.classList.add('atc-loading');
      button.disabled = true;
    }
  }

  /**
   * Hides loading state on the add to cart button.
   */
  hideLoading() {
    const button = this.refs.addToCartButton;
    if (button) {
      button.classList.remove('atc-loading');
      button.disabled = false;
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  async handleClick(event) {
    logMessage('log', 'Add to cart button clicked');
    
    // Prevent form submission since we handle it in the button click
    event.preventDefault();
    
    const form = this.closest('form');
    if (!form) {
      logMessage('error', 'No form found');
      return;
    }
    
    // Check if this form has already been validated by ProductFormComponent
    if (form.dataset.deliveryValidated === 'true') {
      logMessage('log', 'Form already validated, proceeding with submission');
      // Trigger the form submission manually
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
      return;
    }
    
    const shouldProceed = await handleProductSubmission(form, event);
    if (!shouldProceed) {
      return;
    }
    
    logMessage('log', 'Proceeding with normal button click flow');
    
    // Mark the form as validated to prevent duplicate checks
    form.dataset.deliveryValidated = 'true';
    
    // Trigger the form submission manually
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    form.dispatchEvent(submitEvent);
    
    // Only trigger animations if the form submission was successful
    // The animations will be triggered by the form submission success
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    const captureDeliveryMoment = this.dataset.captureDeliveryMoment;

    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);
    
    // Reset delivery validation flag for new submissions
    const productForm = this.querySelector('form');
    if (productForm) {
      delete productForm.dataset.deliveryValidated;
    }

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Show loading state
    this.refs.addToCartButtonContainer?.showLoading();


    // Check postal code and delivery time before proceeding with form submission
    if (!productForm) {
      logMessage('error', 'No form found');
      this.refs.addToCartButtonContainer?.hideLoading();
      return;
    }
    
    if (captureDeliveryMoment === 'true') {
      // Check if delivery validation has already been done by AddToCartComponent
      if (productForm.dataset.deliveryValidated === 'true') {
        logMessage('log', 'Delivery already validated by AddToCartComponent, skipping check');
        // Clear the flag for future submissions
        delete productForm.dataset.deliveryValidated;
      } else {
        const shouldProceed = await handleProductSubmission(productForm, event);
        if (!shouldProceed) {
          this.refs.addToCartButtonContainer?.hideLoading();
          return;
        }
      }
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];

    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      const sectionsWithCollectionFooter = mainContent.querySelectorAll('[id^="shopify-section-"]:has(custom-collection-footer-component)');
      sectionsWithCollectionFooter.forEach(section => {
        const sectionId = section.id.replace('shopify-section-', '');
        cartItemComponentsSectionIds.push(sectionId);
        logMessage('log', 'Adding section containing collection footer:', sectionId);
      });

      // Add delivery moment section if cart is empty (first item being added)
      // if (cartItemsComponents.length == 1) {
        const deliveryMomentSection = document.querySelector('delivery-moment-component')?.closest('[id^="shopify-section-"]');
        if (deliveryMomentSection) {
          const sectionId = deliveryMomentSection.id.replace('shopify-section-', '');
          cartItemComponentsSectionIds.push(sectionId);
          logMessage('log', 'Adding delivery moment section due to empty cart:', sectionId);
        }
      // }
    }

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          // Clear delivery validation flag on error to allow retry
          if (productForm) {
            delete productForm.dataset.deliveryValidated;
          }

          // Hide loading state on error
          this.refs.addToCartButtonContainer?.hideLoading();

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // Trigger animations only on successful cart addition
          if (this.refs.addToCartButtonContainer) {
            this.refs.addToCartButtonContainer.animateAddToCart();
            const target = /** @type {HTMLElement} */ (event.target);
            if (!target?.closest('.quick-add-modal')) {
              this.refs.addToCartButtonContainer.animateFlyToCart();
            }
            // Hide loading state on success
            this.refs.addToCartButtonContainer.hideLoading();
          }

          logMessage('log', 'Cart add response:', response);
          const newSections = response.sections;
          Object.entries(newSections).forEach(([sectionId, sectionHtml]) => {
            if(sectionId && sectionHtml) {
              try {
                morphSection(sectionId, sectionHtml);
              } catch (error) {
                logMessage('warn', `Failed to morph section ${sectionId}:`, error);
              }
            }
            else {
              logMessage('warn', `Section ${sectionId} not found in response`);
            }
          });

          const productUrl = response.url;
          if (!productUrl) {
            logMessage('warn', 'No product URL found');
            return;
          }

          const productTileTemplateUrl = `${productUrl}&view=product-card`;
          const oldProductCard = document.querySelector(`product-card-link[data-product-id="${response.product_id}"]`);
          
          fetch(productTileTemplateUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              return response.text();
            })
            .then((responseText) => {
              const productTileTemplate = new DOMParser().parseFromString(responseText, 'text/html');
              const productCard = (productTileTemplate).querySelector('product-card-link[data-product-handle]');
              
              if (!productCard) {
                logMessage('warn', 'No product template found. Available elements:');
              } else {
                const newProductCard = productCard.querySelector('product-card');
                const oldProductCardElement = oldProductCard?.querySelector('product-card');
                if (oldProductCardElement && newProductCard) {
                  oldProductCardElement.innerHTML = newProductCard.innerHTML;
                  const variantSelectDropdownElement = /** @type {VariantSelectDropdown | null} */ (oldProductCardElement.querySelector('variant-select-dropdown'));
                  const variantSelectDropdown = oldProductCardElement.querySelector('variant-select-dropdown');
                  if(variantSelectDropdownElement && variantSelectDropdown) {
                    const variantToSelect = variantSelectDropdown.querySelector(`.dropdown-menu-item[data-variant-id="${response.variant_id}"]`);
                    const variantIndex = variantToSelect ? variantToSelect.getAttribute("data-index") || '0' : '0';
                    if (variantSelectDropdownElement && variantIndex) {
                      variantSelectDropdownElement.selectItem(parseInt(variantIndex));
                    }
                  }
                }
              }
            })
            .catch((error) => {
              logMessage('error', 'Error fetching product tile template:', error);
            });
        }
      })
      .catch((error) => {
        logMessage('error', 'Cart add error:', error);
        // Clear delivery validation flag on error to allow retry
        if (productForm) {
          delete productForm.dataset.deliveryValidated;
        }
        // Hide loading state on error
        this.refs.addToCartButtonContainer?.hideLoading();
      })
      .finally(() => {
        // Clear delivery validation flag after operation completes
        if (productForm) {
          delete productForm.dataset.deliveryValidated;
        }
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

// Make functions globally available
/** @type {any} */ (window).handleProductSubmission = handleProductSubmission;
